import type { FailureReason } from '../types/run-enums';
import { FAILURE_REASONS } from '../types/run-enums';

import type { StatusTone } from './run-status';
import { humanizeEnum } from './run-status';

/** Plain-language meaning of one failure reason, plus what to do about it. */
export interface FailureReasonInfo {
  label: string;
  /** What happened, in words a non-engineer can act on. */
  explanation: string;
  /** The usual next step — the service groups reasons by exactly this. */
  nextStep: string;
  /**
   * Badge tone by what fixes it: `warning` = fix the product data, `info` =
   * temporary (retry), `error` = change the prompt/content, `muted` = not a fault.
   */
  tone: StatusTone;
}

/**
 * Explanations sourced from the service's `FailureReason` docstring and the
 * workers that write each value (validation rules, generator alias map).
 */
const FAILURE_REASON_INFO: Record<FailureReason, FailureReasonInfo> = {
  SOURCE_MISSING: {
    label: 'Missing product data',
    explanation:
      'The catalog did not have the product information needed to write this field, so there was nothing to write from.',
    nextStep: 'Fill in the product in your catalog, then run it again.',
    tone: 'warning',
  },
  SOURCE_INVALID: {
    label: 'Unusable product data',
    explanation:
      'The product information was there, but in a form the AI could not use (for example malformed or contradictory values).',
    nextStep: 'Correct the product in your catalog, then run it again.',
    tone: 'warning',
  },
  MODEL_ERROR: {
    label: 'AI model error',
    explanation:
      'The request reached the AI model, but it came back with an error or an answer that could not be used.',
    nextStep: 'Usually temporary — retry. If it keeps happening, check the model and instructions.',
    tone: 'info',
  },
  MODEL_TIMEOUT: {
    label: 'AI model timed out',
    explanation: 'The AI model took too long to answer, so the request was abandoned.',
    nextStep: 'Temporary — retry later.',
    tone: 'info',
  },
  RATE_LIMITED: {
    label: 'Too many requests',
    explanation:
      'The AI provider asked us to slow down because too many requests were sent at once.',
    nextStep: 'Temporary — retry later, or run smaller batches.',
    tone: 'info',
  },
  CONTENT_FILTERED: {
    label: 'Blocked by safety filter',
    explanation: "The AI provider's content filter blocked the answer for this product.",
    nextStep:
      'Review the product data or the instructions — retrying unchanged will usually fail again.',
    tone: 'error',
  },
  VALIDATION_FAILED: {
    label: 'Failed scoring rules',
    explanation:
      'The AI wrote something, but it broke a rule for this channel — for example empty, too short or too long, banned phrases, all capitals, or too many bullets.',
    nextStep: 'Adjust the instructions or the product data; the failure message names the rule.',
    tone: 'error',
  },
  GATEWAY_ERROR: {
    label: 'AI gateway error',
    explanation:
      'The connection that passes requests to the AI models rejected the request or could not be reached.',
    nextStep: 'Usually temporary — retry. If it persists, contact support.',
    tone: 'info',
  },
  CANCELLED: {
    label: 'Cancelled',
    explanation: 'The run was stopped before this product finished.',
    nextStep: 'Nothing is wrong with the product — start a new run to write it.',
    tone: 'muted',
  },
  UNKNOWN: {
    label: 'Unknown cause',
    explanation: 'The product failed but no specific cause was recorded.',
    nextStep: 'Read the failure message for clues.',
    tone: 'muted',
  },
};

function isKnownReason(reason: string): reason is FailureReason {
  return (FAILURE_REASONS as readonly string[]).includes(reason);
}

/**
 * Info for a reason off the wire. A value this build has never heard of
 * (a newer worker mid-deploy) degrades to its humanised name, never throws.
 */
export function failureReasonInfo(reason: string): FailureReasonInfo {
  if (isKnownReason(reason)) return FAILURE_REASON_INFO[reason];
  return {
    label: humanizeEnum(reason),
    explanation: 'A failure reason this app does not recognise yet.',
    nextStep: 'Read the failure message for details.',
    tone: 'muted',
  };
}

/** Short plain label for a failure reason, for chips and table cells. */
export function failureReasonLabel(reason: string): string {
  return failureReasonInfo(reason).label;
}
